// src/components/forms/PropertyForm.tsx — create or edit a property (a scheme / development).
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useEntityOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, csvToList, nulled, numberOr, toMajor, toMinor, trimmed } from './common';

type P = Record<string, any>;

const STAGES = ['LAND', 'PLANNING', 'CONSTRUCTION', 'LEASE_UP', 'STABILISED'] as const;
const STAGE_HINT: Record<string, string> = {
  LAND: 'Site acquired, nothing consented yet',
  PLANNING: 'In for consent',
  CONSTRUCTION: 'On site',
  LEASE_UP: 'Complete and letting',
  STABILISED: 'Fully operational',
};

const blank = {
  name: '', region: 'UK', entityId: '', city: '', postcode: '', line1: '', line2: '', storeys: '', apartmentTypes: '',
  latitude: '', longitude: '', lifecycleStage: 'LEASE_UP', assetClass: 'RESIDENTIAL', tenureModel: 'BTR',
  gdv: '', gdvCurrency: 'GBP', practicalCompletionDate: '', description: '', amenities: '', isActive: true,
};

const fromProperty = (p: P) => ({
  name: p.name ?? '', region: p.region ?? 'UK', entityId: p.entityId ?? '', city: p.city ?? '', postcode: p.postcode ?? '',
  line1: p.address?.line1 ?? '', line2: p.address?.line2 ?? '',
  // A storeys figure read off the buildings is not the development's own: leave the field blank so saving keeps it so.
  storeys: p.storeys == null || p.storeysFromBuildings ? '' : String(p.storeys), apartmentTypes: p.apartmentTypes ?? '',
  latitude: p.latitude == null ? '' : String(p.latitude), longitude: p.longitude == null ? '' : String(p.longitude),
  lifecycleStage: p.lifecycleStage ?? 'LEASE_UP', assetClass: p.assetClass ?? 'RESIDENTIAL', tenureModel: p.tenureModel ?? 'BTR',
  gdv: toMajor(p.gdvMinor), gdvCurrency: p.gdvCurrency ?? 'GBP',
  practicalCompletionDate: p.practicalCompletionDate ?? '', description: p.description ?? '',
  amenities: (p.amenities ?? []).join(', '), isActive: p.isActive ?? true,
});

/**
 * One form for both create and edit. The lifecycle stage only appears on create:
 * afterwards it moves through PATCH /properties/:id/stage, which validates the
 * transition — so it is an action on the scheme, not a field on a form.
 */
export function PropertyForm({ open, onClose, property, onSaved }: { open: boolean; onClose: () => void; property?: P; onSaved?: (p: P) => void }) {
  const isNew = !property;
  const qc = useQueryClient();
  const entities = useEntityOptions();
  const [form, setForm] = useState(() => (property ? fromProperty(property) : blank));
  const set = (patch: Partial<typeof blank>) => setForm((f) => ({ ...f, ...patch }));

  const payload = () => ({
    name: form.name.trim(),
    region: form.region,
    entityId: nulled(form.entityId),
    city: nulled(form.city),
    postcode: nulled(form.postcode),
    storeys: numberOr(form.storeys) ?? null,
    apartmentTypes: nulled(form.apartmentTypes),
    address: { line1: trimmed(form.line1), line2: trimmed(form.line2), city: trimmed(form.city), postcode: trimmed(form.postcode), country: form.region === 'AE' ? 'AE' : 'GB' },
    latitude: numberOr(form.latitude) ?? null,
    longitude: numberOr(form.longitude) ?? null,
    assetClass: form.assetClass,
    tenureModel: form.tenureModel,
    gdvMinor: toMinor(form.gdv) ?? null,
    gdvCurrency: form.gdvCurrency,
    practicalCompletionDate: nulled(form.practicalCompletionDate),
    description: nulled(form.description),
    amenities: csvToList(form.amenities),
    isActive: form.isActive,
    ...(isNew ? { lifecycleStage: form.lifecycleStage } : {}),
  });

  const save = useMutation({
    mutationFn: async () => (isNew
      ? (await api.post<P>('/properties', payload())).data
      : (await api.patch<P>(`/properties/${property!.id}`, payload())).data),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['/properties'] });
      void qc.invalidateQueries({ queryKey: ['property', property?.id] });
      void qc.invalidateQueries({ queryKey: ['options', '/properties'] });
      onSaved?.(saved);
      onClose();
    },
  });

  const entityChoices = (entities.data ?? []).filter((e) => e.region === form.region);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isNew ? 'New development' : `Edit ${property!.name}`}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!form.name.trim() || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Create development' : 'Save changes'}</Button>
      </>}
    >
      <div className="space-y-4">
        <FormGrid>
          <Wide><Field label="Development name" hint="The name residents and the website will see"><Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Elgar House" className="w-full" /></Field></Wide>

          <Field label="Region"><Select value={form.region} onChange={(e) => set({ region: e.target.value, entityId: '', gdvCurrency: e.target.value === 'AE' ? 'AED' : 'GBP' })} className="w-full"><option value="UK">United Kingdom</option><option value="AE">United Arab Emirates</option></Select></Field>
          <Field label="Company" hint="Whose books this development runs through"><Select value={form.entityId} onChange={(e) => set({ entityId: e.target.value })} className="w-full"><option value="">Unassigned</option>{entityChoices.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select></Field>

          <Field label="Town or city"><Input value={form.city} onChange={(e) => set({ city: e.target.value })} placeholder="Streatham" className="w-full" /></Field>
          <Field label="Postcode"><Input value={form.postcode} onChange={(e) => set({ postcode: e.target.value })} placeholder="SW16 1AA" className="w-full" /></Field>

          <Field label="No. of storeys" hint={property?.storeysFromBuildings ? `Blank uses the tallest building (${property.storeys})` : undefined}><Input value={form.storeys} onChange={(e) => set({ storeys: e.target.value.replace(/[^0-9]/g, '') })} placeholder={property?.storeysFromBuildings ? String(property.storeys) : '5'} inputMode="numeric" className="w-full" /></Field>
          <Field label="Apartment types" hint="e.g. Studio/1-2B/Duplex 1B"><Input value={form.apartmentTypes} onChange={(e) => set({ apartmentTypes: e.target.value })} maxLength={120} className="w-full" /></Field>

          <Field label="Address line 1"><Input value={form.line1} onChange={(e) => set({ line1: e.target.value })} className="w-full" /></Field>
          <Field label="Address line 2"><Input value={form.line2} onChange={(e) => set({ line2: e.target.value })} className="w-full" /></Field>

          <Field label="Asset class"><Select value={form.assetClass} onChange={(e) => set({ assetClass: e.target.value })} className="w-full">{['RESIDENTIAL', 'COMMERCIAL', 'MIXED', 'INDUSTRIAL'].map((a) => <option key={a} value={a}>{a[0] + a.slice(1).toLowerCase()}</option>)}</Select></Field>
          <Field label="Model" hint="Retained and let, or built for sale"><Select value={form.tenureModel} onChange={(e) => set({ tenureModel: e.target.value })} className="w-full"><option value="BTR">Build to rent</option><option value="BTS">Build to sell</option></Select></Field>

          {isNew && (
            <Wide>
              <Field label="Lifecycle stage" hint={STAGE_HINT[form.lifecycleStage]}>
                <div className="flex flex-wrap gap-1.5">
                  {STAGES.map((s) => (
                    <button key={s} type="button" onClick={() => set({ lifecycleStage: s })} className={`h-7 rounded-lg border px-2.5 text-[12px] transition ${form.lifecycleStage === s ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'}`}>
                      {s.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              </Field>
            </Wide>
          )}

          <Field label={`Gross development value (${form.gdvCurrency})`}><Input value={form.gdv} onChange={(e) => set({ gdv: e.target.value })} placeholder="12500000" inputMode="decimal" className="w-full" /></Field>
          <Field label="Practical completion"><Input type="date" value={form.practicalCompletionDate} onChange={(e) => set({ practicalCompletionDate: e.target.value })} className="w-full" /></Field>

          <Field label="Latitude"><Input value={form.latitude} onChange={(e) => set({ latitude: e.target.value })} placeholder="51.4279" inputMode="decimal" className="w-full" /></Field>
          <Field label="Longitude"><Input value={form.longitude} onChange={(e) => set({ longitude: e.target.value })} placeholder="-0.1287" inputMode="decimal" className="w-full" /></Field>

          <Wide><Field label="Description" hint="Used on the website development page"><Textarea rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} className="w-full" /></Field></Wide>
          <Wide><Field label="Amenities" hint="Comma separated — Concierge, Gym, Roof terrace"><Input value={form.amenities} onChange={(e) => set({ amenities: e.target.value })} className="w-full" /></Field></Wide>

          <Wide>
            <label className="flex items-center gap-2.5 text-[13px] text-ink-700">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} className="h-4 w-4 accent-ink-900" />
              Active — an inactive property stays in the records but drops out of day-to-day lists
            </label>
          </Wide>
        </FormGrid>

        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
